import { useState } from "react";
import { motion } from "framer-motion";
import { Buildings, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHead } from "@/components/site/Promotions";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WHOLESALE_IMG =
  "https://images.unsplash.com/photo-1668933314437-c348b38d6836?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const perks = [
  "Tiered volume pricing up to 40% off retail",
  "Net-30 terms for approved accounts",
  "Priority stock allocation & same-day pickup",
  "Dedicated account manager on WhatsApp",
];

const initial = { business_name: "", contact_name: "", email: "", phone: "", tax_id: "", monthly_volume: "", message: "" };

export const Wholesale = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.phone || !form.monthly_volume) {
      toast.error("Please complete the required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/wholesale", form);
      setDone(true);
      toast.success("Application received!", { description: "Our team will reach out within 2 business days." });
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or WhatsApp us." });
    }
    setSubmitting(false);
  };

  return (
    <section id="wholesale" className="relative py-20 sm:py-28 bg-background z-10" data-testid="wholesale">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead index="03" kicker="Trade & Fleet Accounts" title="Become A Wholesale Partner" />

        <div className="grid lg:grid-cols-2 gap-6 mt-12">
          {/* Left visual + perks */}
          <div className="relative overflow-hidden border border-border min-h-[300px] flex flex-col justify-end">
            <img src={WHOLESALE_IMG} alt="Tire close up" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/30" />
            <div className="relative p-8">
              <Buildings size={36} weight="duotone" className="text-primary mb-4" />
              <h3 className="font-display font-black text-3xl uppercase tracking-tight">Built for volume buyers</h3>
              <ul className="mt-6 space-y-3">
                {perks.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-foreground/85">
                    <CheckCircle size={20} weight="fill" className="text-primary shrink-0 mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <div className="border border-border bg-card p-6 sm:p-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center py-12"
                data-testid="wholesale-success"
              >
                <CheckCircle size={64} weight="fill" className="text-primary" />
                <h3 className="font-display font-black text-3xl uppercase tracking-tight mt-4">Application Sent</h3>
                <p className="text-muted-foreground mt-2 max-w-sm">
                  Thanks — we've logged your request. Expect a call or WhatsApp from our trade desk within 2 business days.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="mt-6 text-sm font-bold uppercase tracking-wide text-primary hover:underline"
                  data-testid="wholesale-another"
                >
                  Submit another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="wholesale-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Business name *" testid="ws-business">
                    <Input value={form.business_name} onChange={set("business_name")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="ws-business-input" />
                  </Field>
                  <Field label="Contact name *" testid="ws-contact">
                    <Input value={form.contact_name} onChange={set("contact_name")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="ws-contact-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email *" testid="ws-email">
                    <Input type="email" value={form.email} onChange={set("email")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="ws-email-input" />
                  </Field>
                  <Field label="Phone *" testid="ws-phone">
                    <Input value={form.phone} onChange={set("phone")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="ws-phone-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Tax / Reseller ID" testid="ws-tax">
                    <Input value={form.tax_id} onChange={set("tax_id")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="ws-tax-input" />
                  </Field>
                  <Field label="Monthly volume *" testid="ws-volume">
                    <Select value={form.monthly_volume} onValueChange={(v) => setForm({ ...form, monthly_volume: v })}>
                      <SelectTrigger className="bg-background border-border rounded-sm h-11 focus:ring-primary focus:ring-1" data-testid="ws-volume-trigger">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        {["Under 50 tires", "50 – 200 tires", "200 – 500 tires", "500+ tires"].map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Tell us about your business" testid="ws-message">
                  <Textarea value={form.message} onChange={set("message")} rows={3} className="bg-background border-border rounded-sm focus-visible:ring-primary focus-visible:ring-1 resize-none" data-testid="ws-message-input" />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white font-bold py-4 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide disabled:opacity-60"
                  data-testid="ws-submit"
                >
                  {submitting ? "Submitting..." : <>Apply For An Account <ArrowRight size={18} weight="bold" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">{label}</label>
    {children}
  </div>
);
